import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Loader2,
  Search,
} from 'lucide-react';
import SubtleFooter from '../components/SubtleFooter';
import {
  getPublicIntakeApi,
  searchPublicIntakePlayersApi,
  submitPublicIntakeResponseApi,
  verifyPublicIntakeCodeApi,
  type PublicIntakePlayer,
  type PublicIntakeSession,
  type PublicIntakeWelcome,
  type TeamIntakeQuestion,
} from '../lib/teamIntakeApi';

const extractPublicIntakeToken = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  try {
    const url = new URL(trimmed);
    return url.pathname.split('/').filter(Boolean).pop() || '';
  } catch {
    return trimmed.replace(/^\/?intake\//, '').replace(/^\/+/, '');
  }
};

const formatDate = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('nl-NL');
};

const PublicTeamIntake: React.FC = () => {
  const navigate = useNavigate();
  const { publicToken: routeToken = '' } = useParams<{ publicToken: string }>();
  const [manualToken, setManualToken] = useState('');
  const publicToken = routeToken || manualToken.trim();
  const [welcome, setWelcome] = useState<PublicIntakeWelcome | null>(null);
  const [session, setSession] = useState<PublicIntakeSession | null>(null);
  const [accessCode, setAccessCode] = useState('');
  const [playerSearch, setPlayerSearch] = useState('');
  const [players, setPlayers] = useState<PublicIntakePlayer[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<PublicIntakePlayer | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [playerLoading, setPlayerLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!routeToken) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void getPublicIntakeApi(routeToken)
      .then(data => { if (!cancelled) setWelcome(data); })
      .catch(() => { if (!cancelled) setError('Deze intake kon niet worden gevonden.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [routeToken]);

  useEffect(() => {
    if (!session || !publicToken || !accessCode.trim() || playerSearch.trim().length < 2) {
      setPlayers([]);
      return;
    }
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      setPlayerLoading(true);
      void searchPublicIntakePlayersApi({
        publicToken,
        accessCode: accessCode.trim(),
        search: playerSearch.trim(),
      })
        .then(data => { if (!cancelled) setPlayers(data); })
        .catch(() => { if (!cancelled) setPlayers([]); })
        .finally(() => { if (!cancelled) setPlayerLoading(false); });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [accessCode, playerSearch, publicToken, session]);

  const questions = session?.questions || [];
  const currentQuestion: TeamIntakeQuestion | null = questions[questionIndex] || null;
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined;
  const canContinue = useMemo(
    () => Boolean(currentQuestion && (!currentQuestion.required || String(currentAnswer ?? '').trim())),
    [currentAnswer, currentQuestion],
  );

  const openManualToken = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const token = extractPublicIntakeToken(manualToken);
    if (token) navigate(`/intake/${encodeURIComponent(token)}`);
  };

  const verifyCode = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!publicToken || loading) return;
    setLoading(true);
    setError(null);
    try {
      const data = await verifyPublicIntakeCodeApi(publicToken, accessCode.trim());
      setSession(data);
      setWelcome(data.run);
    } catch {
      setError('De code klopt niet of deze intake is niet meer open.');
    } finally {
      setLoading(false);
    }
  };

  const submitResponse = async () => {
    if (!publicToken || !selectedPlayer || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitPublicIntakeResponseApi({
        publicToken,
        accessCode: accessCode.trim(),
        playerId: selectedPlayer.id,
        answers,
      });
      setComplete(true);
    } catch {
      setError('Versturen is niet gelukt. Probeer het opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  const panelClass = 'border-y border-gray-200 bg-white px-5 py-5 shadow-sm sm:rounded-lg sm:border';

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <main className="flex-1 px-0 py-6 sm:px-6 sm:py-12">
        <div className="mx-auto w-full max-w-lg space-y-5">
          <div className="flex min-h-16 items-center justify-center px-5">
            <img src="/we-dohockey-orange-black-trans.png" alt="doHockey" className="h-12 w-auto object-contain" />
          </div>

          {!routeToken && (
            <section className={panelClass}>
              <div className="flex items-center gap-3">
                <ClipboardList size={22} className="text-primary-600" />
                <h1 className="text-xl font-semibold text-gray-950">Open je intake</h1>
              </div>
              <p className="mt-3 text-sm leading-6 text-gray-600">
                Gebruik de link die je coach heeft gedeeld, of plak hier de intake-code uit de link.
              </p>
              <form onSubmit={openManualToken} className="mt-4 space-y-3">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">Intake-link of linkcode</span>
                  <input
                    value={manualToken}
                    onChange={event => setManualToken(event.target.value)}
                    className="min-h-11 w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </label>
                <button className="min-h-11 w-full rounded-md bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700">
                  Intake openen
                </button>
              </form>
            </section>
          )}

          {routeToken && !session && !complete && (
            <section className={panelClass}>
              <div className="flex items-start gap-3">
                <ClipboardList size={22} className="mt-0.5 shrink-0 text-primary-600" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-primary-600">Teamintake</p>
                  <h1 className="mt-1 text-xl font-semibold text-gray-950">{welcome?.title || 'Welkom bij de intake'}</h1>
                  <div className="mt-1 flex flex-wrap gap-x-3 text-sm text-gray-500">
                    {welcome?.season && <span>{welcome.season}</span>}
                    {welcome?.due_date && <span>Deadline {formatDate(welcome.due_date)}</span>}
                  </div>
                </div>
              </div>
              <p className="mt-4 text-sm leading-6 text-gray-600">Vul de code van je coach in om te starten.</p>
              <form onSubmit={verifyCode} className="mt-4 space-y-3">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">Toegangscode</span>
                  <input
                    value={accessCode}
                    onChange={event => setAccessCode(event.target.value.toUpperCase())}
                    autoComplete="one-time-code"
                    className="min-h-12 w-full rounded-md border border-gray-300 px-3 text-center text-lg font-semibold uppercase tracking-[0.22em] outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </label>
                <button
                  disabled={loading || !accessCode.trim()}
                  className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {loading && <Loader2 size={17} className="animate-spin" />}
                  Starten
                </button>
              </form>
            </section>
          )}

          {session && !selectedPlayer && !complete && (
            <section className={panelClass}>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-primary-600">Stap 1</p>
              <h1 className="mt-1 text-xl font-semibold text-gray-950">Wie ben jij?</h1>
              <p className="mt-2 text-sm leading-6 text-gray-600">Begin met typen en kies je naam uit de lijst.</p>
              <label className="mt-4 flex min-h-12 items-center gap-2 rounded-md border border-gray-300 px-3 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
                <Search size={18} className="text-gray-400" />
                <input
                  value={playerSearch}
                  onChange={event => setPlayerSearch(event.target.value)}
                  placeholder="Je naam"
                  className="min-w-0 flex-1 bg-transparent text-base outline-none"
                />
                {playerLoading && <Loader2 size={16} className="animate-spin text-gray-400" />}
              </label>
              <div className="mt-3 divide-y divide-gray-200 border-y border-gray-200">
                {players.map(player => (
                  <button
                    key={player.id}
                    type="button"
                    onClick={() => setSelectedPlayer(player)}
                    className="flex min-h-12 w-full items-center justify-between gap-3 py-3 text-left hover:bg-gray-50"
                  >
                    <span className="font-semibold text-gray-900">{player.name}</span>
                    {player.number && <span className="text-sm text-gray-500">Nr. {player.number}</span>}
                  </button>
                ))}
              </div>
            </section>
          )}

          {session && selectedPlayer && currentQuestion && !complete && (
            <section className={panelClass}>
              <div className="flex items-center justify-between gap-3 text-xs font-semibold text-gray-500">
                <span>{selectedPlayer.name}</span>
                <span>Vraag {questionIndex + 1} van {questions.length}</span>
              </div>
              <div className="mt-3 h-1 overflow-hidden rounded-full bg-gray-200">
                <div className="h-full bg-primary-600" style={{ width: `${((questionIndex + 1) / questions.length) * 100}%` }} />
              </div>
              <h1 className="mt-5 text-xl font-semibold leading-7 text-gray-950">{currentQuestion.label}</h1>
              {currentQuestion.type === 'select' ? (
                <div className="mt-4 divide-y divide-gray-200 border-y border-gray-200">
                  {(currentQuestion.options || []).map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setAnswers(current => ({ ...current, [currentQuestion.id]: option }))}
                      className={`min-h-12 w-full px-2 py-3 text-left text-sm font-semibold ${currentAnswer === option ? 'bg-primary-50 text-primary-700' : 'text-gray-800 hover:bg-gray-50'}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              ) : (
                <textarea
                  value={String(currentAnswer ?? '')}
                  onChange={event => setAnswers(current => ({ ...current, [currentQuestion.id]: event.target.value }))}
                  rows={5}
                  className="mt-4 w-full rounded-md border border-gray-300 px-3 py-2 text-base outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                />
              )}
              <div className="mt-5 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setQuestionIndex(current => Math.max(0, current - 1))}
                  disabled={questionIndex === 0}
                  className="inline-flex min-h-11 items-center gap-2 rounded-md border border-gray-300 px-4 text-sm font-semibold text-gray-700 disabled:opacity-40"
                >
                  <ChevronLeft size={17} /> Vorige
                </button>
                {questionIndex < questions.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => setQuestionIndex(current => current + 1)}
                    disabled={!canContinue}
                    className="inline-flex min-h-11 items-center gap-2 rounded-md bg-primary-600 px-4 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    Volgende <ChevronRight size={17} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={submitResponse}
                    disabled={!canContinue || submitting}
                    className="inline-flex min-h-11 items-center gap-2 rounded-md bg-primary-600 px-4 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {submitting ? <Loader2 size={17} className="animate-spin" /> : <CheckCircle2 size={17} />}
                    Versturen
                  </button>
                )}
              </div>
            </section>
          )}

          {complete && (
            <section className={`${panelClass} border-emerald-200 bg-emerald-50 text-emerald-800`}>
              <CheckCircle2 size={28} />
              <h1 className="mt-3 text-xl font-semibold">Bedankt!</h1>
              <p className="mt-2 text-sm leading-6">Je antwoorden zijn verstuurd naar je coach.</p>
            </section>
          )}

          {error && (
            <div role="alert" className="mx-5 border-l-2 border-error-500 bg-error-50 px-4 py-3 text-sm text-error-700 sm:mx-0">
              {error}
            </div>
          )}
        </div>
      </main>
      <SubtleFooter />
    </div>
  );
};

export default PublicTeamIntake;
